// src/pages/BlogArticle.js
import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BlogContext } from '../BlogContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ReactMarkdown from 'react-markdown';

// Helper function to safely decode Base64
const safeBase64Decode = (str) => {
  try {
    return decodeURIComponent(atob(str));
  } catch {
    return str; // Return original if not Base64
  }
};

const toArray = (input, isTable = false) => {
  if (Array.isArray(input)) return input;
  if (typeof input !== 'string') return [];

  if (input.trim().startsWith('[')) {
    try { return JSON.parse(input); } catch { /* ignore */ }
  }

  if (!isTable) {
    const delimiter = input.includes('|') ? '|' : ',';
    return input.split(delimiter).map(safeBase64Decode).map(s => s.trim());
  } else {
    const rowDelimiter = input.includes('||') ? '||' : ';';
    const cellDelimiter = input.includes('|') && !input.includes('||') ? '|' : ',';
    return input.split(rowDelimiter).map(r => r.split(cellDelimiter).map(c => safeBase64Decode(c.trim())));
  }
};

const processContentSections = (content) => {
  return (content || []).map(section => {
    if (section.type === 'list') {
      return {
        ...section,
        items: toArray(section.items)
      };
    }
    if (section.type === 'table') {
      return {
        ...section,
        headers: toArray(section.headers),
        rows: toArray(section.rows, true)
      };
    }
    if (section.type === 'timeline') {
      const events = toArray(section.events);
      return {
        ...section,
        events: events.map(event => ({
          ...event,
          content: event.content.map(contentSection => {
            if (contentSection.type === 'list') {
              return {
                ...contentSection,
                items: toArray(contentSection.items)
              };
            }
            return contentSection;
          })
        }))
      };
    }
    return section;
  });
};

const BlogArticle = () => {
  const { id } = useParams();
  const { getPostById, trackView } = useContext(BlogContext);
  const post = getPostById(id);

  // Generate structured data for SEO
  const generateStructuredData = (post) => {
    if (!post) return null;

    const baseData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt || post.seoDescription,
      "image": post.headerImage ? [`/vendor/${post.headerImage}`] : ["/vendor/pfp.png"],
      "datePublished": post.publishedDate || post.createdAt,
      "dateModified": post.createdAt,
      "author": {
        "@type": "Person",
        "name": post.author,
        "url": "https://kobypierce.com"
      },
      "publisher": {
        "@type": "Person",
        "name": "Koby Pierce",
        "url": "https://kobypierce.com"
      }
    };

    // Check if post contains timelines
    const hasTimeline = post.content?.some(section => section.type === 'timeline');

    if (hasTimeline) {
      const timelineSections = post.content.filter(section => section.type === 'timeline');

      baseData["@type"] = "HowTo";
      baseData["step"] = [];

      timelineSections.forEach((section, sectionIndex) => {
        section.events?.forEach((event, eventIndex) => {
          baseData.step.push({
            "@type": "HowToStep",
            "position": sectionIndex * 10 + eventIndex + 1,
            "name": event.title,
            "text": event.date || "",
            "description": event.content?.find(c => c.type === 'text')?.value?.substring(0, 300) || event.title
          });
        });
      });

      baseData["name"] = post.title;
      baseData["description"] = post.excerpt || `Timeline article with ${baseData.step.length} steps`;
    }

    return baseData;
  };

  useEffect(() => {
    if (post && post.id) {
      trackView(post.id);
    }
  }, [post, trackView]);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      {post && (
        <SEO
          title={post.seoTitle || `${post.title} - Koby Pierce Blog`}
          description={post.seoDescription || post.excerpt || `Read ${post.title} by ${post.author}. ${post.timeToRead} read.`}
          keywords={post.seoKeywords || `${post.title}, ${post.author}, Koby Pierce, Blog, Timeline, ${post.content.map(c => c.title).filter(Boolean).join(', ')}`}
          image={post.headerImage ? `/vendor/${post.headerImage}` : "/vendor/pfp.png"}
          url={`https://kobypierce.com/blog/${post.id}`}
          type="article"
          structuredData={generateStructuredData(post)}
        />
      )}
      <Header />
      
      {/* Hero Section with Header Image */}
      {post && post.headerImage && (
        <div className="relative w-full h-[60vh] overflow-hidden">
          <img
            src={`/vendor/${post.headerImage}`}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          {/* Dark overlay for better visual appeal */}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          {/* Top gradient overlay */}
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0d0d0d] to-transparent"></div>
          {/* Bottom gradient overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0d0d0d] to-transparent"></div>
        </div>
      )}

      {/* Title and metadata section below header image */}
      {post && post.headerImage && (
        <div className="w-full bg-[#0d0d0d] py-8 -mt-[110px] relative z-10">
          <div className="max-w-4xl mx-auto px-4">
            <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-300">
              <span>By {post.author}</span>
              <span>•</span>
              <span>{post.timeToRead} read</span>
              {post.publishedDate && (
                <>
                  <span>•</span>
                  <span>{new Date(post.publishedDate + "T00:00:00").toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main container with content */}
      <div className="w-full bg-[#0d0d0d] min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {post ? (
            <article className="pb-8">
              {/* If no header image, add top spacing to prevent title from being covered */}
              {!post.headerImage && (
                <div className="pt-2">
                  <h1 className="text-4xl font-bold mb-2">{post.title}</h1>
                  <p className="text-gray-400 mb-4">
                    By {post.author} — {post.timeToRead} read
                  </p>
                  {post.publishedDate && (
                    <p className="text-gray-400 mb-4">
                      Published on {new Date(post.publishedDate + "T00:00:00").toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            
            {post.excerpt && (
              <p className="text-xl text-gray-300 mb-8 italic leading-relaxed">
                {post.excerpt}
              </p>
            )}
            <div className="space-y-6">
              {processContentSections(post.content).map((section, index) => (
                <div key={index}>
                  {section.title && (
                    <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                  )}
                  
                  {section.type === 'text' && (
                    <div className="prose prose-invert max-w-none">
                      <ReactMarkdown>{section.value}</ReactMarkdown>
                    </div>
                  )}
                  
                  {section.type === 'image' && section.fileName && (
                    <div className={`${
                      section.size === 'small' ? 'w-1/3' : 
                      section.size === 'medium' ? 'w-2/3' : 
                      'w-full'
                    } ${
                      section.alignment === 'left' ? 'mr-auto' :
                      section.alignment === 'right' ? 'ml-auto' :
                      'mx-auto'
                    }`}>
                      <img
                        src={`/vendor/${section.fileName}`}
                        alt={section.alt || ''}
                        className="w-full h-auto rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {section.type === 'list' && section.items && (
                    <ul className="list-disc ml-6 space-y-2">
                      {toArray(section.items).map((item, i) => (
                        <li key={i} className="text-gray-300">
                          <div className="prose prose-invert max-w-none inline">
                            <ReactMarkdown components={{
                              p: ({ children }) => <span>{children}</span>
                            }}>
                              {item}
                            </ReactMarkdown>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.type === 'table' && section.headers && section.rows && (
                    <table className="w-full border-collapse border border-[#4d4d4d]">
                      <thead>
                        <tr className="bg-[#1a1a1a]">
                          {toArray(section.headers).map((header, i) => (
                            <th key={i} className="border border-[#4d4d4d] p-3 text-left">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {toArray(section.rows, true).map((row, i) => (
                          <tr key={i}>
                            {row.map((cell, j) => (
                              <td key={j} className="border border-[#4d4d4d] p-3">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {section.type === 'timeline' && section.events && (
                    <div className="relative py-8">
                      {/* Timeline line */}
                      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#555555]"></div>

                      <div className="space-y-12" role="list" aria-label={section.title || "Timeline events"}>
                        {(Array.isArray(section.events) ? section.events : []).map((event, eventIndex) => (
                          <div key={eventIndex} className="relative flex" role="listitem">
                            {/* Timeline dot - positioned to center on the line */}
                            <div
                              className="absolute left-6 -translate-x-1/2 z-10 w-8 h-8 bg-[#666666] rounded-full flex items-center justify-center text-white font-medium text-sm"
                              aria-hidden="true"
                            >
                              {eventIndex + 1}
                            </div>

                            {/* Timeline content */}
                            <div className="flex-1 pb-8 ml-16">
                              <article className="bg-[#1a1a1a] rounded-lg p-5 border border-[#333333]">
                                <header>
                                  <h3
                                    className="text-xl font-semibold text-white mb-1"
                                    id={`timeline-event-${eventIndex}`}
                                  >
                                    {event.title}
                                  </h3>
                                  {event.date && (
                                    <time
                                      className="text-gray-400 text-sm mb-4 block"
                                      dateTime={event.date}
                                      aria-label={`Event date: ${event.date}`}
                                    >
                                      {event.date}
                                    </time>
                                  )}
                                </header>

                                <div className="space-y-4">
                                  {(event.content || []).map((contentSection, contentIndex) => (
                                    <div key={contentIndex}>
                                      {contentSection.type === 'text' && (
                                        <div
                                          className="prose prose-invert max-w-none text-gray-300 leading-relaxed"
                                          aria-labelledby={`timeline-event-${eventIndex}`}
                                        >
                                          <ReactMarkdown>{contentSection.value}</ReactMarkdown>
                                        </div>
                                      )}

                                      {contentSection.type === 'image' && contentSection.fileName && (
                                        <figure className="my-4">
                                          <img
                                            src={`/vendor/${contentSection.fileName}`}
                                            alt={contentSection.alt || event.title}
                                            className="w-full h-auto rounded-lg shadow-md"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                          {contentSection.alt && (
                                            <figcaption className="text-gray-400 text-xs mt-2 sr-only">
                                              {contentSection.alt}
                                            </figcaption>
                                          )}
                                        </figure>
                                      )}

                                      {contentSection.type === 'list' && contentSection.items && (
                                        <ul
                                          className="list-disc ml-6 space-y-2 text-gray-300"
                                          aria-labelledby={`timeline-event-${eventIndex}`}
                                        >
                                          {toArray(contentSection.items).map((item, i) => (
                                            <li key={i}>
                                              <div className="prose prose-invert max-w-none inline">
                                                <ReactMarkdown components={{
                                                  p: ({ children }) => <span>{children}</span>
                                                }}>
                                                  {item}
                                                </ReactMarkdown>
                                              </div>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </article>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </article>
        ) : (
          <div className="p-8">
            <h1>Post not found</h1>
          </div>
        )}
        </div>
      </div>
      {/* Footer container with 2/3 width */}
      <div className="w-full md:w-2/3 mx-auto">
        <Footer />
      </div>
      {/* Extra bottom padding */}
      <div className="pb-8"></div>
    </div>
  );
};

export default BlogArticle;